import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import {
  FileDownload as DownloadIcon,
  Description as DocumentIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ValidatorIcon,
  Groups as CoordinatorIcon,
} from "@mui/icons-material";
import { colors } from "../theme/colors";
import { UserContext } from "../context/UserContext";

const Documentation = () => {
  const { user } = useContext(UserContext);
  const [selectedManual, setSelectedManual] = useState(null);
  const [availableManuals, setAvailableManuals] = useState([]);

  // Definizione di tutti i manuali disponibili
  const allManuals = [
    {
      id: 1,
      title: "Manuale Utente Admin",
      icon: <AdminIcon />,
      color: "#EA4335", // Red
      description:
        "Documentazione completa per gli utenti con ruolo di Amministratore",
      fileName: "TechExpenses GUIDE Admin.pdf",
      role: "Admin",
    },
    {
      id: 2,
      title: "Manuale Utente Validatore",
      icon: <ValidatorIcon />,
      color: "#4285F4", // Blue
      description:
        "Documentazione completa per gli utenti con ruolo di Validatore",
      fileName: "TechExpenses GUIDE Validator.pdf",
      role: "Validator",
    },
    {
      id: 3,
      title: "Manuale Utente Coordinatore",
      icon: <CoordinatorIcon />,
      color: "#34A853", // Green
      description:
        "Documentazione completa per gli utenti con ruolo di Coordinatore",
      fileName: "TechExpenses GUIDE Coordinator.pdf",
      role: "Coordinator",
    },
  ];

  // Filtra i manuali in base al ruolo dell'utente all'inizializzazione
  useEffect(() => {
    if (user && user.userRoles) {
      // Se l'utente è Admin, mostra solo il manuale Admin
      const isAdmin = user.userRoles.some((role) => role.roleName === "Admin");
      if (isAdmin) {
        const adminManual = allManuals.find(
          (manual) => manual.role === "Admin"
        );
        setAvailableManuals([adminManual]);
        setSelectedManual(adminManual);
        return;
      }

      // Se l'utente è Validator, mostra solo il manuale Validator
      const isValidator = user.userRoles.some(
        (role) => role.roleName === "Validator"
      );
      if (isValidator) {
        const validatorManual = allManuals.find(
          (manual) => manual.role === "Validator"
        );
        setAvailableManuals([validatorManual]);
        setSelectedManual(validatorManual);
        return;
      }

      // Se l'utente è Coordinator, mostra solo il manuale Coordinator
      const isCoordinator = user.userRoles.some(
        (role) => role.roleName === "Coordinator"
      );
      if (isCoordinator) {
        const coordinatorManual = allManuals.find(
          (manual) => manual.role === "Coordinator"
        );
        setAvailableManuals([coordinatorManual]);
        setSelectedManual(coordinatorManual);
        return;
      }

      // Se non è stato trovato alcun ruolo specifico, non mostrare alcun manuale
      setAvailableManuals([]);
      setSelectedManual(null);
    }
  }, [user]);

  const handleManualSelect = (manual) => {
    setSelectedManual(manual);
  };

  const handleDownload = (fileName) => {
    // Create a link element
    const link = document.createElement("a");

    // Set the href attribute to the file path
    // This assumes the PDF files are stored in the /public/manuals/ directory
    link.href = `/manuals/${encodeURIComponent(fileName)}`;

    // Set the download attribute to suggest the filename for download
    link.download = fileName;

    // Append the link to the document body
    document.body.appendChild(link);

    // Trigger the click event on the link
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Q8Breadcrumbs
        links={[{ label: "Documentazione", href: "/documentation" }]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Documentazione Tecnica
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ flex: 1, overflow: "hidden" }}>
        {/* Left column - Manual list */}
        <Grid item xs={4}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: 3,
            }}
          >
            <Box
              sx={{
                p: 2,
                background: colors.primary.main,
                color: "white",
                borderBottom: `3px solid ${colors.warning.main}`,
              }}
            >
              <Typography variant="h6">Manuale Disponibile</Typography>
            </Box>
            <List sx={{ flex: 1, overflow: "auto" }}>
              {availableManuals.map((manual) => (
                <React.Fragment key={manual.id}>
                  <ListItem
                    button
                    onClick={() => handleManualSelect(manual)}
                    selected={selectedManual?.id === manual.id}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: `${manual.color}20`,
                        borderLeft: `4px solid ${manual.color}`,
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: `${manual.color}30`,
                      },
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: manual.color }}>
                      {manual.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={manual.title}
                      primaryTypographyProps={{
                        fontWeight:
                          selectedManual?.id === manual.id ? "bold" : "normal",
                      }}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Card>
        </Grid>

        {/* Right column - Manual details */}
        <Grid item xs={8}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: 3,
            }}
          >
            {selectedManual ? (
              <>
                <Box
                  sx={{
                    p: 2,
                    background: selectedManual.color,
                    color: "white",
                    borderBottom: `3px solid ${colors.warning.main}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {selectedManual.icon}
                    <Typography variant="h6">{selectedManual.title}</Typography>
                  </Box>
                  <Q8Button
                    variant="tertiary"
                    label="Scarica PDF"
                    icon={<DownloadIcon />}
                    onClick={() => handleDownload(selectedManual.fileName)}
                    sx={{
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    p: 3,
                    flex: 1,
                    overflow: "auto",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Descrizione
                  </Typography>
                  <Typography paragraph>
                    {selectedManual.description}
                  </Typography>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Contenuti principali
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <DocumentIcon />
                      </ListItemIcon>
                      <ListItemText primary="Homepage e accesso al portale" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DocumentIcon />
                      </ListItemIcon>
                      <ListItemText primary="Gestione OPEX" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DocumentIcon />
                      </ListItemIcon>
                      <ListItemText primary="Gestione CAPEX" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DocumentIcon />
                      </ListItemIcon>
                      <ListItemText primary="Report" />
                    </ListItem>
                    {selectedManual.role === "Admin" && (
                      <>
                        <ListItem>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText primary="Gestione Utenti" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText primary="Log Admin" />
                        </ListItem>
                      </>
                    )}
                    {(selectedManual.role === "Admin" ||
                      selectedManual.role === "Validator") && (
                      <>
                        <ListItem>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText primary="Configurazioni Conti" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText primary="Configurazioni CDC" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText primary="Configurazione Voci Opex" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText primary="Livelli CAPEX" />
                        </ListItem>
                      </>
                    )}
                    {selectedManual.role === "Coordinator" && (
                      <>
                        <ListItem>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText primary="Visualizzazione Conti Tech" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <DocumentIcon />
                          </ListItemIcon>
                          <ListItemText primary="Visualizzazione CDC" />
                        </ListItem>
                      </>
                    )}
                  </List>

                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <Q8Button
                      variant="primary"
                      label="Scarica Manuale Completo"
                      icon={<DownloadIcon />}
                      onClick={() => handleDownload(selectedManual.fileName)}
                      size="large"
                    />
                  </Box>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  p: 3,
                  textAlign: "center",
                }}
              >
                <DocumentIcon
                  sx={{ fontSize: 80, color: "primary.main", mb: 2 }}
                />
                <Typography variant="h5" gutterBottom>
                  Seleziona un manuale
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Scegli un manuale dalla lista a sinistra per visualizzare i
                  dettagli e scaricarlo.
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Documentation;
